import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { supabase, BlogPost } from '../../lib/supabase';

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const brandFilter = searchParams.get('brand');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = brandFilter
    ? posts.filter((post) => post.brand === brandFilter)
    : posts;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              {brandFilter ? `${brandFilter} の記事` : 'ブログ'}
            </h1>
            <p className="text-xl text-gray-600">
              {brandFilter
                ? `${brandFilter} に関する最新記事一覧`
                : '高級腕時計の世界をお届けします'}
            </p>
            {brandFilter && (
              <div className="mt-6">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 px-5 py-2 border border-amber-300 text-amber-700 text-sm rounded-full hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-close-line text-base"></i>
                  フィルターを解除してすべて見る
                </Link>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-16 h-16 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-500 mb-4">
                {brandFilter
                  ? `${brandFilter} の記事はまだありません`
                  : 'まだ記事がありません'}
              </p>
              {brandFilter && (
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm font-medium transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  すべての記事に戻る
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="group bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-amber-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-64 w-full overflow-hidden">
                    <img
                      src={post.image_url || 'https://readdy.ai/api/search-image?query=luxury%20watch%20elegant%20timepiece%20on%20sophisticated%20dark%20background%20with%20soft%20lighting%20highlighting%20craftsmanship%20and%20premium%20details&width=800&height=600&seq=blog-default&orientation=landscape'}
                      alt={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                      <i className="ri-calendar-line"></i>
                      <span>{formatDate(post.created_at)}</span>
                      <span>•</span>
                      <span>{post.author}</span>
                      {post.brand && (
                        <>
                          <span>•</span>
                          <span className="text-amber-600 font-medium">{post.brand}</span>
                        </>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-amber-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 line-clamp-3 mb-4 text-sm leading-relaxed">
                      {post.content.replace(/<[^>]*>/g, '').substring(0, 150)}...
                    </p>
                    <div className="flex items-center gap-1 text-amber-600 font-semibold text-sm">
                      <span className="whitespace-nowrap">続きを読む</span>
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
